import type { RetryOptions } from "@reduxjs/toolkit/query"
import { AxiosError } from "axios"
import { api } from "@/store/api"
import { ICatalogsResponse } from "@/store/apis/catalogsAPI/catalogs.types"

// Same ladder as worldMapAPI's entry-critical GET — kept local so the two
// endpoints stay independently tunable.
const ENTRY_RETRY_DELAYS_MS = [1000, 2000, 4000]

// Entry-critical retry predicate: retry on network errors and 5xx, up to the
// ladder's length; fail fast on any resolvable 4xx or once attempts run out.
export const shouldRetryEntryCritical = (
  error: unknown,
  attempt: number
): boolean => {
  if (attempt > ENTRY_RETRY_DELAYS_MS.length) return false
  const status = (error as AxiosError).response?.status
  return status === undefined || status >= 500
}

export const catalogsAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getCatalogs: build.query<ICatalogsResponse, void>({
      query: () => ({ url: "/catalogs", method: "GET" }),
      // Reference data is immutable for the session: fetched once at app start
      // and never evicted, so every later reader hits this same cache entry.
      // Deliberately untagged — nothing may invalidate it.
      keepUnusedDataFor: Infinity,
      // Entry-critical idempotent GET (documented carve-out from the "no RTK
      // maxRetries" rule): transient failures (network / 5xx) auto-retry on the
      // ladder above; 4xx fails fast.
      extraOptions: {
        backoff: async (attempt: number, _maxRetries: number) => {
          const delay = ENTRY_RETRY_DELAYS_MS[attempt - 1] ?? 4000
          await new Promise<void>((resolve) => setTimeout(resolve, delay))
        },
        retryCondition: (
          error: unknown,
          _args: unknown,
          extraArgs: { attempt: number }
        ): boolean => shouldRetryEntryCritical(error, extraArgs.attempt)
      } satisfies RetryOptions
    })
  })
})

export const { useGetCatalogsQuery } = catalogsAPI
